import { Dispatch, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../redux/store';
import { handleFileOpenCheck } from '../util/FileUtils';
import {
    resetCheckResult,
    resetFileOpenError,
    showViewFileErrorDialog,
} from '../redux/project/file/sliceProjectFileOpen';
import { useEnqueueSnackbar } from './useEnqueueSnackbar';
import { useTranslation } from 'react-i18next';
import { ToastSeverity } from '../components/General/Toast/Toast';
import { resetLinkDocumentUrlHasError } from '../redux/project/documents/sliceProjectAdditionalDocuments';

const throttleMs = 2000; // ms

export const useFilesOpen = () => {
    const dispatch: Dispatch<unknown> = useDispatch();
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });
    const { error: fileOpenError, checkResults: fileOpenChecks } = useSelector(
        (store: IAppReduxStore) => store.projectFileOpen,
    );
    const { linkDocumentUrlHasError } = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs,
    );

    const enqueueSnackbar = useEnqueueSnackbar();

    // next available time for file-open execution
    const [nextExecution, setNextExecution] = useState<number>(Date.now());

    const handleFileOpenChecks = (newOpenChecks: {
        file: string;
        response: string;
        callbackURL: string;
    }[]) => {
        if (!(newOpenChecks?.length > 0)) return;

        const newestCheck = newOpenChecks.slice().pop();
        dispatch(resetCheckResult({ callbackURL: newestCheck.callbackURL }));

        const remainingMs = nextExecution - Date.now();

        if (remainingMs < (throttleMs * -1)) {
            setNextExecution(Date.now() + throttleMs);
            handleFileOpenCheck(newestCheck);
        }
        else {
            setNextExecution(Date.now() + remainingMs + throttleMs);
            setTimeout(() => handleFileOpenCheck(newestCheck), remainingMs);
        }
    };

    useEffect(() => {
        handleFileOpenChecks(fileOpenChecks);
    }, [fileOpenChecks]);

    useEffect(() => {
        if (!fileOpenError) return;

        if (fileOpenError.message === 'NO_VIEWER_CONFIGURED') {
            dispatch(showViewFileErrorDialog(true));
        } else {
            enqueueSnackbar(t('fileOpenError'), {
                severity: ToastSeverity.Error,
            });
        }
        dispatch(resetFileOpenError());
    }, [fileOpenError]);

    useEffect(() => {
        if (linkDocumentUrlHasError) {
            enqueueSnackbar(t('fileOpenError'), {
                severity: ToastSeverity.Error,
            });
            dispatch(resetLinkDocumentUrlHasError());
        }
    }, [linkDocumentUrlHasError]);
};
